import { Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { memo, useCallback, useMemo, useState } from 'react';
import { hiddenPickerSx } from 'src/components/DatePicker/hidden-picker.styles';
import { ModalContainer, ModalList, type ModalListItem } from 'src/components/ModalContainer';
import { useSubscriptionPause } from 'src/graphql/entities/subscription-pause';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { getFormattedDate, getHumanReadableDate } from 'src/utils/date';
import { useNotifications } from 'src/utils/hooks';
import { getDateFromNaturalLanguage } from 'src/utils/natural-language-date';
import { capitalizeString } from 'src/utils/strings';

type PauseDate = () => string;

interface PauseOption extends ModalListItem {
  date?: PauseDate;
}

const CUSTOM_OPTION_ID = 'custom';

const PRESETS: { id: string; text: string; date: PauseDate }[] = [
  {
    id: 'one-week',
    text: 'One week',
    date: () => dayjs().add(1, 'week').endOf('day').toISOString(),
  },
  {
    id: 'two-weeks',
    text: 'Two weeks',
    date: () => dayjs().add(2, 'weeks').endOf('day').toISOString(),
  },
  {
    id: 'one-month',
    text: 'One month',
    date: () => dayjs().add(1, 'month').endOf('day').toISOString(),
  },
  {
    id: 'three-months',
    text: 'Three months',
    date: () => dayjs().add(3, 'months').endOf('day').toISOString(),
  },
];

const getDefaultCustomDate = () => dayjs().add(1, 'week');

function toOption(id: string, title: string, date: PauseDate): PauseOption {
  return {
    id,
    title,
    date,
    endAdornment: (
      <Typography variant="caption" color="text.secondary" noWrap>
        {getFormattedDate(date())}
      </Typography>
    ),
  };
}

function parsePauseDate(text: string): PauseDate | null {
  const parsedDate = getDateFromNaturalLanguage(text, { dayStart: '11:59pm' });
  return parsedDate ? () => dayjs(parsedDate).endOf('day').toISOString() : null;
}

export const PauseSubscriptionModal = () => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [input, setInput] = useState('');
  const [customDate, setCustomDate] = useState<dayjs.Dayjs | null>(getDefaultCustomDate);

  const { isOpened, modalProps } = useModalState(Modals.PauseSubscription);
  const { closeModal } = useModalMethods(Modals.PauseSubscription);
  const { pauseSubscription } = useSubscriptionPause();
  const { showErrorAlert } = useNotifications();

  const options = useMemo<PauseOption[]>(() => {
    if (input) {
      const parsedDate = parsePauseDate(input);
      return parsedDate
        ? [
            toOption(
              'parsed',
              capitalizeString(getHumanReadableDate(parsedDate(), false)),
              parsedDate,
            ),
          ]
        : [];
    }
    return [
      ...PRESETS.map(({ id, text, date }) => toOption(id, text, date)),
      { id: CUSTOM_OPTION_ID, title: 'Custom' },
    ];
  }, [input]);

  const submitPause = useCallback(
    (date?: string) => {
      const address = modalProps?.address;
      if (!date || !address?.addressId || !address?.partnerId) {
        return;
      }

      const diff = new Date(date).getTime() - new Date().getTime();
      if (diff <= 0) {
        showErrorAlert('The pause date must be in the future');
        return;
      }

      closeModal();
      void pauseSubscription({
        addressId: address.addressId,
        partnerId: address.partnerId,
        pauseUntil: date,
        planName: address.clientActivatedMembership,
      });
    },
    [closeModal, showErrorAlert, pauseSubscription, modalProps],
  );

  const handleSelect = (option: PauseOption) => {
    if (option.id === CUSTOM_OPTION_ID) {
      setPickerOpen(true);
      return;
    }
    submitPause(option.date?.());
  };

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Pause subscription"
      onClose={closeModal}
      data-testid="pause-subscription-modal"
    >
      <ModalList
        items={options}
        shouldFilter={false}
        onSearchChange={setInput}
        onSelect={handleSelect}
        placeholder="Try 2 weeks, next month, Aug 16th"
        emptyText="No date matches that text."
        testId="pause-subscription-modal"
      />
      <DatePicker
        label="Custom date"
        disablePast
        minDate={dayjs().add(1, 'day')}
        open={pickerOpen}
        value={customDate}
        onChange={(newValue) => setCustomDate(newValue)}
        onAccept={(value) => {
          setPickerOpen(false);
          submitPause(value?.endOf('day').toISOString());
          setCustomDate(getDefaultCustomDate());
        }}
        onOpen={() => setPickerOpen(true)}
        onClose={() => {
          setPickerOpen(false);
          setCustomDate(getDefaultCustomDate());
        }}
        aria-hidden="true"
        sx={hiddenPickerSx}
      />
    </ModalContainer>
  );
};

export default memo(PauseSubscriptionModal);
